// src/components/Admin/Campaigns/ai/campaignFlows.ts
//
// Background AI flows for the Campaign Builder admin.
//
// These are deliberately NOT the chat. They run on their own, produce structured
// content, and never appear in any chat transcript. Two consequences follow:
//
//   - Each flow uses its own stable, per-flow, per-campaign session_id, so its
//     prompts (and the JSON envelopes it asks for) never pollute a conversation
//     the admin is having in the Guide. Sharing a session would poison the chat.
//   - Every flow degrades to `null` rather than throwing. The builder still
//     renders from its own form state if the model is unavailable; the AI makes
//     the content sharper, it is not load-bearing.
//
// All flows reuse the app's existing AI plumbing: `fetchStream('ai/chat/stream',
// body, { signal })` (auth handled internally via getApiHeaders) drained with the
// same SSE loop the ResourceEditorDialog uses, then structured JSON is extracted
// from the accumulated reply text.

import { fetchStream } from '../../../../support/queries';
import { DEFAULT_AI_MODEL_VALUE } from '../../../../support/aiModelOptions';
import {
  Campaign,
  CampaignStep,
  STEP_KINDS,
  STEP_TRACKS,
  StepKind,
  StepTrack,
} from '../../../../support/campaigns';

// ---------------------------------------------------------------------------
// Draft types
// ---------------------------------------------------------------------------

export type StepStub = {
  name: string;
  description?: string;
  track?: string;
  step_kind?: string;
  is_required?: boolean;
  children?: StepStub[];
};

export type CampaignOutlineDraft = {
  name: string;
  description: string;
  estimated_duration_minutes?: number;
  steps: StepStub[];
};

// ---------------------------------------------------------------------------
// Allowed enum values, sourced from campaigns.ts so they stay in lock-step.
// ---------------------------------------------------------------------------

const ALLOWED_STEP_KINDS = new Set<string>(STEP_KINDS.map((k) => k.value));
const ALLOWED_STEP_TRACKS = new Set<string>(STEP_TRACKS.map((t) => t.value));

// ---------------------------------------------------------------------------
// SSE frame parsing — replicated from ResourceEditorDialog.streamRegenerateDescription
// ---------------------------------------------------------------------------

type SseEventName =
  | 'status'
  | 'token'
  | 'final'
  | 'error'
  | 'done'
  | 'message'
  | 'ping';

const parseSseFrames = (buffer: string): { frames: string[]; remaining: string } => {
  const frames: string[] = [];
  let remaining = buffer;
  let idx = remaining.indexOf('\n\n');

  while (idx >= 0) {
    frames.push(remaining.slice(0, idx));
    remaining = remaining.slice(idx + 2);
    idx = remaining.indexOf('\n\n');
  }

  return { frames, remaining };
};

const parseSseEvent = (
  raw: string
): { eventName: SseEventName; data: any } | null => {
  let eventName: SseEventName = 'message';
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim() as SseEventName;
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (!dataLines.length) return null;

  const dataStr = dataLines.join('\n');
  try {
    return { eventName, data: JSON.parse(dataStr) };
  } catch {
    return null;
  }
};

/**
 * POST to the streaming endpoint, drain the SSE stream, and return the full
 * accumulated assistant text. Prefers the authoritative `final` response when
 * the backend sends one, otherwise falls back to the concatenated tokens.
 *
 * Throws on any transport/stream error; callers wrap it and degrade to null.
 */
const streamText = async (body: any, signal?: AbortSignal): Promise<string> => {
  const resp = await fetchStream('ai/chat/stream', body, { signal });

  if (!resp.ok) {
    throw new Error(`Streaming request failed (${resp.status})`);
  }
  if (!resp.body) {
    throw new Error('Streaming response has no body');
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let tokenText = '';
  let finalText = '';
  let streamDone = false;

  const handleEvent = (eventName: SseEventName, data: any) => {
    switch (eventName) {
      case 'token': {
        const t = data?.text ?? data?.token ?? data?.content ?? '';
        if (t) tokenText += t;
        return;
      }
      case 'final':
        if (typeof data?.response === 'string') finalText = data.response;
        return;
      case 'error':
        throw new Error(data?.message || data?.error || 'Generation failed');
      case 'done':
        streamDone = true;
        return;
      default:
        // status / ping / message — nothing rendered for a background flow.
        return;
    }
  };

  try {
    while (!streamDone) {
      const r = await reader.read();
      if (r.done) break;

      buffer += decoder.decode(r.value, { stream: true });

      const { frames, remaining } = parseSseFrames(buffer);
      buffer = remaining;

      for (const f of frames) {
        const evt = parseSseEvent(f);
        if (!evt) continue;
        handleEvent(evt.eventName, evt.data);
      }
    }

    if (buffer.trim()) {
      const evt = parseSseEvent(buffer.trim());
      if (evt) handleEvent(evt.eventName, evt.data);
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // no-op
    }
  }

  return finalText || tokenText;
};

// ---------------------------------------------------------------------------
// JSON extraction + coercion
// ---------------------------------------------------------------------------

/**
 * Pull the first structured JSON object out of a reply.
 *
 * Prefers a fenced ```json block; if that is absent or unparseable, slices from
 * the first `{` to the last `}` and tries that. Returns null on any failure so
 * callers never have to try/catch.
 */
export function extractJson<T = any>(text: string): T | null {
  if (!text || typeof text !== 'string') return null;

  const fenced = text.match(/```json\s*([\s\S]*?)```/i);
  const candidate = fenced ? fenced[1] : text;

  try {
    return JSON.parse(candidate) as T;
  } catch {
    const start = candidate.indexOf('{');
    const end = candidate.lastIndexOf('}');
    if (start === -1 || end <= start) return null;
    try {
      return JSON.parse(candidate.slice(start, end + 1)) as T;
    } catch {
      return null;
    }
  }
}

const cleanString = (value: unknown, maxLen: number): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, maxLen);
};

const cleanBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const cleanDuration = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.min(Math.round(n), 100000);
};

const clampTrack = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const v = value.trim();
  return ALLOWED_STEP_TRACKS.has(v) ? v : undefined;
};

const clampKind = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const v = value.trim();
  return ALLOWED_STEP_KINDS.has(v) ? v : undefined;
};

/**
 * Coerce one raw entry into a StepStub, dropping anything invalid. Recurses into
 * children up to a bounded depth so a runaway reply cannot build an infinite tree.
 */
const coerceStub = (raw: unknown, depth = 0): StepStub | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  const name = cleanString(r.name, 200);
  if (!name) return null; // a step with no name is not usable

  const stub: StepStub = { name };

  const description = cleanString(r.description, 2000);
  if (description) stub.description = description;

  const track = clampTrack(r.track);
  if (track) stub.track = track;

  const kind = clampKind(r.step_kind);
  if (kind) stub.step_kind = kind;

  const required = cleanBoolean(r.is_required);
  if (required !== undefined) stub.is_required = required;

  if (depth < 4 && Array.isArray(r.children)) {
    const children = coerceStubs(r.children, depth + 1);
    if (children.length) stub.children = children;
  }

  return stub;
};

const coerceStubs = (value: unknown, depth = 0): StepStub[] => {
  if (!Array.isArray(value)) return [];
  const out: StepStub[] = [];
  for (const entry of value) {
    const stub = coerceStub(entry, depth);
    if (stub) out.push(stub);
    if (out.length >= 40) break;
  }
  return out;
};

// ---------------------------------------------------------------------------
// Grounding / prompt helpers
// ---------------------------------------------------------------------------

const STYLE =
  'Write for an internal enablement/delivery admin building a reusable campaign, in plain ' +
  'professional English. Ground strictly on the campaign and step data provided below. Never ' +
  'invent customer names, product facts, or metrics that are not given. If something is unknown, ' +
  'leave it out rather than fabricating it.';

const flowSessionId = (flow: string, campaignId: number | string): string =>
  `campaign-flow-${flow}-${campaignId}`;

const stepLine = (step: CampaignStep, indent: string): string =>
  `${indent}- ${step.name} [track=${step.track}, kind=${step.step_kind}` +
  `${step.is_required ? ', required' : ''}]`;

const outlineExisting = (steps: CampaignStep[]): string => {
  if (!steps || !steps.length) return 'No steps have been created yet.';
  // Flat list is enough grounding here; the builder owns the true tree.
  return steps.map((s) => stepLine(s, '')).join('\n');
};

// ---------------------------------------------------------------------------
// Flows
// ---------------------------------------------------------------------------

/** Draft a whole campaign outline (name/description/duration + a step tree) from a title + blurb. */
export async function generateCampaignOutline(
  input: { name: string; description?: string },
  signal?: AbortSignal
): Promise<CampaignOutlineDraft | null> {
  try {
    const name = (input.name || '').trim();
    if (!name) return null;

    const shape = JSON.stringify(
      {
        name: 'A clear campaign title.',
        description: 'Two or three sentences describing the campaign.',
        estimated_duration_minutes: 120,
        steps: [
          {
            name: 'Step title',
            description: 'What this step covers.',
            track: `one of: ${STEP_TRACKS.map((t) => t.value).join(', ')}`,
            step_kind: `one of: ${STEP_KINDS.map((k) => k.value).join(', ')}`,
            is_required: true,
            children: [],
          },
        ],
      },
      null,
      2
    );

    const question = [
      'You are drafting the outline for a reusable enablement/delivery campaign.',
      `Working title: ${name}`,
      input.description?.trim()
        ? `Author notes: ${input.description.trim()}`
        : 'No further notes were provided.',
      '',
      'Produce a coherent campaign: a refined name, a short description, an estimated total ' +
        'duration in minutes, and a hierarchical list of steps. Group related steps using a ' +
        '"group" step_kind with children. Keep the tree focused (roughly 3-8 top-level steps).',
      STYLE,
      '',
      'Reply with ONLY a fenced ```json block in exactly this shape:',
      '```json',
      shape,
      '```',
    ].join('\n');

    const text = await streamText(
      {
        question,
        context: `Working title: ${name}\n${input.description?.trim() || ''}`,
        related_type: 'campaign',
        related_id: 'new-campaign',
        product: name,
        session_id: flowSessionId('outline', 'new'),
        use_drift: false,
        model_id: DEFAULT_AI_MODEL_VALUE,
      },
      signal
    );

    const parsed = extractJson<Record<string, unknown>>(text);
    if (!parsed || typeof parsed !== 'object') return null;

    const draft: CampaignOutlineDraft = {
      name: cleanString(parsed.name, 200) || name,
      description: cleanString(parsed.description, 2000) || '',
      steps: coerceStubs(parsed.steps),
    };
    const duration = cleanDuration(parsed.estimated_duration_minutes);
    if (duration !== undefined) draft.estimated_duration_minutes = duration;

    // A reply that parsed but carried nothing useful is the same as no reply.
    if (!draft.description && draft.steps.length === 0) return null;
    return draft;
  } catch {
    return null;
  }
}

/** Suggest child steps to add under a given parent (or at the root) of an existing campaign. */
export async function suggestChildSteps(
  input: {
    campaign: Campaign;
    parentStep: CampaignStep | null;
    existingSteps: CampaignStep[];
  },
  signal?: AbortSignal
): Promise<StepStub[] | null> {
  try {
    const { campaign, parentStep, existingSteps } = input;
    if (!campaign) return null;

    const shape = JSON.stringify(
      {
        steps: [
          {
            name: 'Step title',
            description: 'What this step covers.',
            track: `one of: ${STEP_TRACKS.map((t) => t.value).join(', ')}`,
            step_kind: `one of: ${STEP_KINDS.map((k) => k.value).join(', ')}`,
            is_required: false,
          },
        ],
      },
      null,
      2
    );

    const question = [
      `You are extending an existing campaign named "${campaign.name}".`,
      campaign.description ? `Campaign description: ${campaign.description}` : '',
      parentStep
        ? `Suggest steps to add UNDER the parent step "${parentStep.name}" ` +
          `(track=${parentStep.track}, kind=${parentStep.step_kind}).`
        : 'Suggest new top-level steps for this campaign.',
      '',
      'Existing steps (do NOT duplicate these):',
      outlineExisting(existingSteps),
      '',
      'Suggest 3-6 additional steps that fill genuine gaps. ' + STYLE,
      '',
      'Reply with ONLY a fenced ```json block in exactly this shape:',
      '```json',
      shape,
      '```',
    ]
      .filter(Boolean)
      .join('\n');

    const text = await streamText(
      {
        question,
        context: `Campaign: ${campaign.name}\n${campaign.description || ''}`,
        related_type: 'campaign',
        related_id: String(campaign.id),
        product: campaign.name,
        session_id: flowSessionId(
          parentStep ? `children-${parentStep.id}` : 'children-root',
          campaign.id
        ),
        use_drift: false,
        model_id: DEFAULT_AI_MODEL_VALUE,
      },
      signal
    );

    const parsed = extractJson<Record<string, unknown>>(text);
    if (!parsed || typeof parsed !== 'object') return null;

    const stubs = coerceStubs(parsed.steps);
    return stubs.length ? stubs : null;
  } catch {
    return null;
  }
}

/** Draft (or rewrite) the description for a single step, grounded on its campaign context. */
export async function generateStepDescription(
  input: { campaign: Campaign; step: CampaignStep },
  signal?: AbortSignal
): Promise<string | null> {
  try {
    const { campaign, step } = input;
    if (!campaign || !step) return null;

    const question = [
      `You are writing the description for one step of the campaign "${campaign.name}".`,
      campaign.description ? `Campaign description: ${campaign.description}` : '',
      `Step name: ${step.name}`,
      `Step track: ${step.track}; step kind: ${step.step_kind}.`,
      step.description ? `Current description: ${step.description}` : '',
      '',
      'Write a clear, concise description (2-4 sentences) of what this step covers and what the ' +
        'participant should get out of it. Return prose only, no headings, no JSON. ' +
        STYLE,
    ]
      .filter(Boolean)
      .join('\n');

    const text = await streamText(
      {
        question,
        context: `Campaign: ${campaign.name}\nStep: ${step.name}\n${step.description || ''}`,
        related_type: 'campaign-step',
        related_id: String(step.id),
        product: campaign.name,
        session_id: flowSessionId(`step-desc-${step.id}`, campaign.id),
        use_drift: false,
        model_id: DEFAULT_AI_MODEL_VALUE,
      },
      signal
    );

    const cleaned = cleanString(text, 2000);
    return cleaned || null;
  } catch {
    return null;
  }
}

/** Draft a short helper note for a single field on the campaign or a step. */
export async function generateFieldNote(
  input: {
    entity: 'campaign' | 'step';
    field: string;
    context: Record<string, unknown>;
  },
  signal?: AbortSignal
): Promise<string | null> {
  try {
    const { entity, field, context } = input;
    if (!entity || !field) return null;

    const contextLines = Object.entries(context || {})
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
      .join('\n');

    const question = [
      `You are helping an admin fill in the "${field}" field of a campaign ${entity}.`,
      'Here is what is already known:',
      contextLines || '(nothing has been entered yet)',
      '',
      `Write a short, practical suggestion for the "${field}" value — one or two sentences, ` +
        'prose only, no headings, no JSON. ' +
        STYLE,
    ].join('\n');

    const idPart =
      context && (context.id !== undefined && context.id !== null)
        ? String(context.id)
        : 'new';

    const text = await streamText(
      {
        question,
        context: contextLines,
        related_type: `campaign-${entity}-field`,
        related_id: `${idPart}-${field}`,
        product: 'campaign',
        session_id: flowSessionId(`field-${entity}-${field}`, idPart),
        use_drift: false,
        model_id: DEFAULT_AI_MODEL_VALUE,
      },
      signal
    );

    const cleaned = cleanString(text, 1200);
    return cleaned || null;
  } catch {
    return null;
  }
}

/**
 * One AI-suggested pool question. `outcome_title` names the outcome it explores so the caller can
 * map it back to a `CampaignOutcome.id` (null / unmatched = a general-pool question); `context`
 * mirrors the customer/internal split. Kept title-keyed rather than index-keyed so a reordered or
 * partially-suggested reply still resolves correctly.
 */
export type QuestionSuggestion = {
  text: string;
  outcome_title: string | null;
  context: 'customer' | 'internal';
};

const clampContext = (value: unknown): 'customer' | 'internal' =>
  value === 'internal' ? 'internal' : 'customer';

const coerceQuestionSuggestions = (value: unknown): QuestionSuggestion[] => {
  if (!Array.isArray(value)) return [];
  const out: QuestionSuggestion[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const r = entry as Record<string, unknown>;
    const text = cleanString(r.text, 500);
    if (!text) continue;
    out.push({
      text,
      outcome_title: cleanString(r.outcome_title, 200) ?? null,
      context: clampContext(r.context),
    });
    if (out.length >= 20) break;
  }
  return out;
};

/**
 * Suggest questions for a campaign's question pool, grounded on its prioritized outcomes.
 *
 * The pool is what the AI later draws from during a delivery: for an outcome that already looks
 * achieved it skips the question; for an unmet one it probes *why* (non-blocking — never a
 * prerequisite to move forward). So the suggestions are diagnostic/probing questions tied to the
 * outcomes, split by customer vs internal context, and de-duplicated against the existing pool.
 * Degrades to null like every other flow.
 */
export async function suggestPoolQuestions(
  input: {
    campaign: Campaign;
    outcomes: Array<{ title: string; description?: string; context: string }>;
    existingQuestions: string[];
  },
  signal?: AbortSignal
): Promise<QuestionSuggestion[] | null> {
  try {
    const { campaign, outcomes, existingQuestions } = input;
    if (!campaign || !outcomes?.length) return null;

    const shape = JSON.stringify(
      {
        questions: [
          {
            text: 'A single probing question.',
            outcome_title: 'The exact outcome title this explores, or null for the general pool.',
            context: 'one of: customer, internal',
          },
        ],
      },
      null,
      2
    );

    const outcomeLines = outcomes
      .map(
        (o, i) =>
          `${i + 1}. [${o.context}] ${o.title}${o.description ? ` — ${o.description}` : ''}`
      )
      .join('\n');

    const question = [
      `You are building the question pool for the campaign "${campaign.name}".`,
      campaign.description ? `Campaign description: ${campaign.description}` : '',
      '',
      'These are the campaign outcomes, highest priority first:',
      outcomeLines,
      '',
      existingQuestions.length
        ? 'Questions already in the pool (do NOT duplicate these):\n' +
          existingQuestions.map((q) => `- ${q}`).join('\n')
        : 'The pool is currently empty.',
      '',
      'For each outcome, suggest one or two probing questions the delivery team can ask to gauge ' +
        'whether the outcome is being met and, if not, understand why. Questions must be diagnostic ' +
        'and non-blocking (never a prerequisite to move forward). Set outcome_title to the exact ' +
        'outcome title the question explores. Keep customer-facing questions in the customer ' +
        'context and internal/Cisco-side questions in the internal context — never mix the two. ' +
        STYLE,
      '',
      'Reply with ONLY a fenced ```json block in exactly this shape:',
      '```json',
      shape,
      '```',
    ]
      .filter(Boolean)
      .join('\n');

    const text = await streamText(
      {
        question,
        context: `Campaign: ${campaign.name}\n${campaign.description || ''}\n\nOutcomes:\n${outcomeLines}`,
        related_type: 'campaign',
        related_id: String(campaign.id),
        product: campaign.name,
        session_id: flowSessionId('question-pool', campaign.id),
        use_drift: false,
        model_id: DEFAULT_AI_MODEL_VALUE,
      },
      signal
    );

    const parsed = extractJson<Record<string, unknown>>(text);
    if (!parsed || typeof parsed !== 'object') return null;

    const suggestions = coerceQuestionSuggestions(parsed.questions);
    return suggestions.length ? suggestions : null;
  } catch {
    return null;
  }
}

// Re-export enum value types purely for callers that want them alongside the flows.
export type { StepKind, StepTrack };
